import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { login as unifiedLogin, identifyEmail } from "@/services/unifiedAuth";
import { sendLoginLink } from "@/services/auth";
import {
  trackLoginSubmitted,
  trackLoginSucceeded,
  trackLoginFailed,
  trackMagicLinkRequested,
  trackLoginEmailIdentified,
} from "@/analytics/analytics";
import AuthLayout from "@/components/AuthLayout";

interface RootLoginProps {
  onLoginSuccess?: () => void;
}

const RootLogin = ({ onLoginSuccess }: RootLoginProps = {}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<"email" | "password" | "magic_link" | "link_sent">("email");
  const [identifyResult, setIdentifyResult] = useState<{
    action: "password" | "magic_link";
    customerName?: string;
    magicLinkAvailable?: boolean;
  } | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) {
      toast({ title: "Missing email", description: "Please enter your email." });
      return;
    }
    try {
      setLoading(true);
      const res = await identifyEmail(email);
      if (res.action === "magic_link") {
        trackLoginEmailIdentified({ action: "magic_link" });
        setIdentifyResult({ action: "magic_link", customerName: res.customerName });
        setStep("magic_link");
      } else {
        trackLoginEmailIdentified({ action: "password" });
        setIdentifyResult({
          action: "password",
          customerName: (res as any)?.customerName,
          magicLinkAvailable: (res as any)?.magicLinkAvailable,
        });
        setStep("password");
      }
    } catch (err: any) {
      toast({
        title: "Error",
        description: String(err?.message || "Unable to identify email"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) {
      toast({ title: "Missing fields", description: "Please enter email and password." });
      return;
    }
    try {
      setLoading(true);
      trackLoginSubmitted({ method: "password" });
      const res = await unifiedLogin(email, password);
      trackLoginSucceeded({ role: res.role, type: "password" });

      if (res.role === "Admin") {
        navigate("/recruit-admin/dashboard", { replace: true });
      } else if (res.role === "Customer") {
        navigate("/customer/dashboard", { replace: true });
      } else if (res.role === "EndUser") {
        // End users go to the main app (Index page)
        if (onLoginSuccess) {
          onLoginSuccess();
        } else {
          navigate("/", { replace: true });
        }
        return;
      } else {
        // Fallback to main app
        navigate("/", { replace: true });
      }
    } catch (err: any) {
      trackLoginFailed();
      toast({
        title: "Login failed",
        description: String(err?.message || "Invalid email or password"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  const taglines = [
    "Matched to programs that fit your game",
    "See where your student actually fits",
    "Personalized matches, not generic lists",
  ];

  const valuePanel = (
    <div className="max-w-md space-y-8 text-white">
      <h2 className="text-3xl xl:text-4xl font-bold leading-tight">
        Your student's college golf matches — based on who each program{" "}
        <span className="text-primary">actually recruits.</span>
      </h2>
      <p className="text-white/70 text-lg">
        Not a database to search. Not templates to follow. A personalized recruiting plan built on
        real commitment data from 2,000+ programs.
      </p>
    </div>
  );

  return (
    <AuthLayout subtitle="Your matches are waiting." taglines={taglines} valuePanel={valuePanel}>
      <Card className="w-full border-0 bg-transparent shadow-none">
        <CardHeader>
          <CardDescription>
            {step === "email"
              ? "Sign in to golfrecruit.ai"
              : step === "password"
                ? "Enter your password"
                : step === "magic_link"
                  ? "Sign in with a login link"
                  : "Check your inbox — login link sent."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === "email" ? (
            <form onSubmit={handleEmailSubmit} className="space-y-3">
              <div>
                <Input
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="email@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Checking…" : "Continue"}
              </Button>
            </form>
          ) : step === "password" ? (
            <form onSubmit={handlePasswordSubmit} className="space-y-3">
              <div>
                <Input type="email" name="email" autoComplete="email" value={email} disabled />
              </div>
              <div>
                <Input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  autoFocus
                  placeholder="Password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Signing in…" : "Sign in"}
              </Button>
              {identifyResult?.magicLinkAvailable && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={loading}
                  onClick={async () => {
                    try {
                      setLoading(true);
                      trackMagicLinkRequested();
                      await sendLoginLink(email);
                      setStep("link_sent");
                    } catch (err: any) {
                      toast({
                        title: "Error",
                        description: String(err?.message || "Failed to send login link"),
                        variant: "destructive",
                      });
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  Send me a login link instead
                </Button>
              )}
              <div className="flex justify-between text-center">
                <Button
                  type="button"
                  variant="link"
                  className="px-0 h-auto text-sm"
                  onClick={() => {
                    setStep("email");
                    setPassword("");
                  }}
                >
                  Change email
                </Button>
                <Button
                  type="button"
                  variant="link"
                  className="px-0 h-auto text-sm"
                  onClick={() => navigate("/forgot-password")}
                >
                  Forgot password?
                </Button>
              </div>
            </form>
          ) : step === "magic_link" ? (
            <div className="space-y-3">
              <div>
                <Input type="email" value={email} disabled />
              </div>
              <div className="text-sm text-muted-foreground">
                {identifyResult?.customerName
                  ? `Your ${identifyResult.customerName} account uses passwordless sign-in.`
                  : "Your account uses passwordless sign-in."}
              </div>
              <Button
                className="w-full"
                disabled={loading}
                onClick={async () => {
                  try {
                    setLoading(true);
                    trackMagicLinkRequested();
                    await sendLoginLink(email);
                    setStep("link_sent");
                  } catch (err: any) {
                    toast({
                      title: "Error",
                      description: String(err?.message || "Failed to send login link"),
                      variant: "destructive",
                    });
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                {loading ? "Sending..." : "Send me a login link"}
              </Button>
              <div className="text-center">
                <Button
                  type="button"
                  variant="link"
                  className="px-0 h-auto text-sm"
                  onClick={() => {
                    setStep("email");
                    setPassword("");
                  }}
                >
                  Change email
                </Button>
              </div>
            </div>
          ) : step === "link_sent" ? (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                Click the link in your email to sign in — no password needed.
              </div>
              <Button variant="outline" className="w-full" onClick={() => setStep("magic_link")}>
                Resend login link
              </Button>
              <div className="text-center">
                <Button
                  type="button"
                  variant="link"
                  className="px-0 h-auto text-sm"
                  onClick={() => {
                    setStep("email");
                    setPassword("");
                  }}
                >
                  Change email
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
      <div className="text-center text-sm text-muted-foreground pb-2 px-6">
        No account?{" "}
        <Link to="/subscribe" className="text-primary-text hover:underline font-semibold">
          Get your matches →
        </Link>
      </div>
    </AuthLayout>
  );
};

export default RootLogin;
